import React, { useRef, useLayoutEffect, useState, useEffect } from "react";
import "./HowItWorks.css";
import * as FaIcons from "react-icons/fa";

const getIconComponent = (iconName) => {
  const IconComponent = FaIcons[iconName];
  if (!IconComponent) {
    console.warn(`Icon '${iconName}' not found in react-icons/fa.`);
    return null;
  }
  return IconComponent;
};

export const vanijyamInfo = {
    howItWorks: {
        heading: "How Vanidya Works: Purity Delivered",
        roles: [
            {
                title: "Customers",
                icon: "FaUserAlt",
                steps: [
                    {
                        title: "Browse Satvik & Organic Essentials",
                        description:
                            "Explore curated categories: Organic groceries, Satvik staples, Pooja items and Spiritual books.",
                    },
                    {
                        title: "Check Product Details",
                        description:
                            "View clear labels, ingredient lists, certifications and usage instructions before adding to your cart.",
                    },
                    {
                        title: "Order & Pay Securely",
                        description:
                            "Place your order with simple checkout. Choose digital payment or cash on delivery.",
                    },
                    {
                        title: "Receive with Peace of Mind",
                        description:
                            "Get your pure, trusted products delivered to your doorstep, ready for your home, rituals, and reading.",
                    },
                ],
            },
            {
                title: "Sellers",
                icon: "FaStore",
                steps: [
                    {
                        title: "Register & List Products",
                        description:
                            "Create your seller profile, add your Satvik, Organic, or Pooja essentials and provide any relevant certifications.",
                    },
                    {
                        title: "Maintain Quality",
                        description:
                            "Keep your listings up to date, ensure accuracy in labeling, and respond to orders promptly.",
                    },
                    {
                        title: "Manage Orders Easily",
                        description:
                            "Track orders, handle packing, and arrange delivery or pickup as required.",
                    },
                    {
                        title: "Reach Mindful Buyers",
                        description:
                            "Get access to local buyers. Grow your business by connecting with customers who value purity, transparency and tradition.",
                    },
                ],
            },
            {
                title: "Vanidya",
                icon: "FaLeaf",
                steps: [
                    {
                        title: "Curate & Approve Listings",
                        description:
                            "We review all product listings to ensure they meet Satvik, organic, and authenticity standards.",
                    },
                    {
                        title: "Support Sellers & Buyers",
                        description:
                            "Provide guidance on certification, clear policies, and responsive support for any order issues.",
                    },
                    {
                        title: "Ensure Honest Shopping",
                        description:
                            "Uphold transparency, ethical sourcing and fair pricing for every user.",
                    },
                ],
            },
        ],
    },
};

const TimelineItem = ({ step, title, description }) => {
  return (
    <div className="timeline-item">
      <div className="timeline-marker-line-connector"></div>
      <div className="timeline-marker">{step}</div>
      <div className="timeline-content">
        <h4 className="timeline-item-title">{title}</h4>
        <p className="timeline-item-description">{description}</p>
      </div>
    </div>
  );
};

const HowItWorks = () => {
  const [activeTab, setActiveTab] = useState(0);
  const tabRefs = useRef([]);
  const [indicatorStyle, setIndicatorStyle] = useState({});
  const timelineRef = useRef(null);

  useLayoutEffect(() => {
    const setIndicator = () => {
      const activeTabNode = tabRefs.current[activeTab];
      if (activeTabNode) {
        setIndicatorStyle({
          width: activeTabNode.offsetWidth,
          transform: `translateX(${activeTabNode.offsetLeft}px)`,
        });
      }
    };
    setIndicator();
    window.addEventListener("resize", setIndicator);
    return () => window.removeEventListener("resize", setIndicator);
  }, [activeTab]);

  useEffect(() => {
    const handleScroll = () => {
      const timeline = timelineRef.current;
      if (!timeline) return;

      const timelineRect = timeline.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const startPoint = viewportHeight * 0.8;
      let fillPercentage = 0;

      if (timelineRect.top < startPoint && timelineRect.bottom > 0) {
        const distanceScrolled = startPoint - timelineRect.top;
        const totalScrollableHeight =
          timelineRect.height + (viewportHeight - startPoint);
        fillPercentage = (distanceScrolled / totalScrollableHeight) * 100;
      } else if (timelineRect.bottom <= 0) {
        fillPercentage = 100;
      }

      timeline.style.setProperty(
        "--timeline-fill-height",
        `${Math.min(100, Math.max(0, fillPercentage))}%`
      );
    };
    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, [activeTab]);

  const activeRoleArray = [vanijyamInfo.howItWorks.roles[activeTab]];
  return (
    <section className="how-it-works-section_">
      <div className="how-it-works-content">
        <h2 className="_hTitle_s5E_ how-it-works-heading">
          {vanijyamInfo.howItWorks.heading}
        </h2>

        <div className="tabs-container">
          <div className="tabs-nav">
            {vanijyamInfo.howItWorks.roles.map((role, index) => {
              const IconComponent = getIconComponent(role.icon);
              return (
                <button
                  key={role.title}
                  ref={(el) => (tabRefs.current[index] = el)}
                  className={`tab-item ${activeTab === index ? "active" : ""}`}
                  onClick={() => setActiveTab(index)}
                >
                  {IconComponent && <IconComponent className="tab-item-icon" />}
                  <span className="tab-item-title">{role.title}</span>
                </button>
              );
            })}
            <div className="active-tab-indicator" style={indicatorStyle} />
          </div>
        </div>

        <div className="tab-content-panel">
          {activeRoleArray.map((role) => (
            <div className="role-timeline-container" key={role.title}>
              <div className="timeline" ref={timelineRef}>
                {role.steps.map((step, stepIndex) => (
                  <TimelineItem
                    key={step.title}
                    step={stepIndex + 1}
                    title={step.title}
                    description={step.description}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
